const STORAGE_KEY = 'referral_attribution';
const ATTRIBUTION_WINDOW_DAYS = 7;

interface ReferralAttribution {
  referral_code: string;
  captured_at: string; // ISO timestamp
}

/**
 * Capture a referral code from URL params into localStorage.
 * First-come-first-served: does NOT overwrite existing attribution.
 */
export function captureReferralCode(code: string): void {
  if (typeof window === 'undefined') return;

  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return; // already have attribution, don't overwrite

    const attribution: ReferralAttribution = {
      referral_code: code.trim(),
      captured_at: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

/**
 * Read the stored referral attribution, if valid (within 7-day window).
 * Returns null if no attribution or expired.
 */
export function getReferralAttribution(): ReferralAttribution | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const attribution: ReferralAttribution = JSON.parse(raw);
    if (!attribution.referral_code || !attribution.captured_at) return null;

    const capturedAt = new Date(attribution.captured_at).getTime();
    const now = Date.now();
    const windowMs = ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60 * 1000;

    if (now - capturedAt > windowMs) {
      // Expired — clean up
      clearReferralAttribution();
      return null;
    }

    return attribution;
  } catch {
    return null;
  }
}

/**
 * Clear stored referral attribution (after successful claim or expiry).
 */
export function clearReferralAttribution(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
